import { useState, useEffect, useMemo } from 'react'
import { Layout, Menu, Avatar, Dropdown, Badge, Space, Button, Select, Tag } from 'antd'
import {
  DashboardOutlined,
  UserOutlined,
  RobotOutlined,
  PictureOutlined,
  DollarOutlined,
  SettingOutlined,
  BellOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  TeamOutlined,
  SafetyOutlined,
  GiftOutlined,
  AppstoreOutlined,
  SendOutlined,
  MessageOutlined,
  FileSearchOutlined,
  ExperimentOutlined,
  AuditOutlined,
  FileImageOutlined,
} from '@ant-design/icons'
import { useNavigate, useLocation, useSearchParams, Outlet } from 'react-router'
import { useAuthStore } from '@/store/authStore'
import { usePackageStore } from '@/store/packageStore'
import { apiClient } from '@/lib/api'
import type { MenuProps } from 'antd'

const { Header, Sider, Content } = Layout

// localStorage key:记住"上次所用 App",仅当 URL 未带 app_id 时作默认值兜底。
const LAST_APP_KEY = 'admin_last_app_id'

type MenuItem = Required<MenuProps>['items'][number]
// 扩展 antd MenuItem 增加可选的 permission 字段。
// 渲染前会按当前用户权限过滤;未声明 permission 的菜单项始终展示(后端 403 仍是最终防线)。
type PermissionMenuItem = MenuItem & {
  permission?: string
  anyOf?: string[]
  children?: PermissionMenuItem[]
}

/**
 * 递归过滤菜单项:
 * - 叶子节点带 permission 但用户没权限 → 移除
 * - 父节点的所有子节点都被过滤掉 → 父节点也移除(空菜单组没意义)
 * - 持有 "*" 通配(super_admin)直接全量返回
 */
function filterMenuByPermission(items: PermissionMenuItem[], permissions: string[]): MenuItem[] {
  if (permissions.includes('*')) return items as MenuItem[]
  const out: MenuItem[] = []
  for (const raw of items) {
    if (!raw) continue
    const item = raw as PermissionMenuItem & { children?: PermissionMenuItem[] }
    // 父级:递归过滤孩子;孩子全没了就丢弃这个父级。
    if (item.children && item.children.length > 0) {
      const kept = filterMenuByPermission(item.children, permissions)
      if (kept.length === 0) continue
      const { permission: _ignored, anyOf: _ignoredAny, ...rest } = item
      out.push({ ...rest, children: kept } as MenuItem)
      continue
    }
    // 叶子节点:看 permission。
    if (item.permission && !permissions.includes(item.permission)) continue
    if (item.anyOf && !item.anyOf.some((code) => permissions.includes(code))) continue
    const { permission: _ignored2, anyOf: _ignoredAny2, ...rest } = item
    out.push(rest as MenuItem)
  }
  return out
}

export default function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false)
  const [openKeys, setOpenKeys] = useState<string[]>([])
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = location.pathname
  const user = useAuthStore((state) => state.user)
  const loading = useAuthStore((state) => state.loading)
  const checkAuth = useAuthStore((state) => state.checkAuth)
  const logout = useAuthStore((state) => state.logout)
  const permissions = useAuthStore((state) => state.permissions)

  // 检查登录状态
  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  // 未登录则跳转到登录页
  useEffect(() => {
    if (!loading && !user) {
      navigate('/login')
    }
  }, [user, loading, navigate])

  // 所有父菜单的 keys
  // 一级折叠组的 key 列表,用于"手风琴"展开行为。每次新增 / 改名一级菜单都要同步这里。
  const rootSubmenuKeys = [
    'user-ops', 'content', 'payment', 'op-activity', 'push', 'rooms', 'prompt-logs',
    'ai-memory', 'image-gen', 'generation-v2', 'image-config', 'app-config', 'system', 'audit',
  ]

  const selectedApp = usePackageStore((state) => state.selectedApp)
  const appList = usePackageStore((state) => state.appList)
  const setSelectedApp = usePackageStore((state) => state.setSelectedApp)
  const setAppList = usePackageStore((state) => state.setAppList)
  const [searchParams, setSearchParams] = useSearchParams()

  // 应用上下文以 URL 的 ?app_id= 为唯一事实源;store 只是它的镜像缓存,
  // 这样新页签/刷新/分享链接都能从 URL 自动初始化选中态,各业务页读 store 即可、无需改动。
  // localStorage 仅作"上次所用 App"的默认值兜底,优先级低于 URL。

  // 获取应用列表;URL 没带 app_id 时,用"上次所用 / 第一个" App 写回 URL(规范化),
  // store 的赋值统一交给下面的 URL→store 同步 effect 处理。
  useEffect(() => {
    const fetchApps = async () => {
      try {
        const response = await apiClient.get<{ apps: Array<{ id: string, package_name: string, name?: string }>, total: number }>('/admin-api/v1/admin/apps/all')
        if (response.code === 0 && response.data.apps) {
          const apps = response.data.apps
          setAppList(apps)
          const urlAppId = new URLSearchParams(window.location.search).get('app_id')
          if (!urlAppId && apps.length > 0) {
            const lastId = localStorage.getItem(LAST_APP_KEY)
            const fallback = apps.find((a) => a.id === lastId) ?? apps[0]
            setSearchParams(
              (prev) => {
                prev.set('app_id', fallback.id)
                return prev
              },
              { replace: true },
            )
          }
        }
      } catch (error) {
        console.error('Failed to fetch apps:', error)
      }
    }
    fetchApps()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // URL → store 同步:URL 带 app_id 时,把它指向的 app 镜像进 store。
  // 注意:app_id 缺失时**不主动清空** —— 既有深链(如 /settings/versions?package=xxx)
  // 用别的参数选 app,清空会把它们误伤;清空只在选择器 onClear 显式发生。
  useEffect(() => {
    if (appList.length === 0) return
    const urlAppId = searchParams.get('app_id')
    if (!urlAppId) return
    const app = appList.find((a) => a.id === urlAppId) ?? null
    if ((selectedApp?.id ?? null) !== (app?.id ?? null)) {
      setSelectedApp(app)
    }
    if (app) localStorage.setItem(LAST_APP_KEY, app.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appList, searchParams])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  // 顶栏头像下拉:用 Dropdown 的 onClick 集中处理,避免每个 menu item 各挂一个回调。
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人信息',
    },
    {
      key: 'change-password',
      icon: <SettingOutlined />,
      label: '修改密码',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true,
    },
  ]
  const handleUserMenuClick: MenuProps['onClick'] = (e) => {
    switch (e.key) {
      case 'profile':
        navigate('/profile')
        break
      case 'change-password':
        navigate('/profile/password')
        break
      case 'logout':
        handleLogout()
        break
    }
  }

  // 菜单按"业务职能"重排:
  //   用户运营 / 内容管理 / 支付管理 / 运营活动 / 推送 / 聊天管理 /
  //   AI · 记忆 / 图片生成 / 图片配置 / 应用配置 / 系统管理 / 审计
  //
  // 路由路径(key)沿用原值,避免现有外链 / 书签失效;仅调整菜单层级 / 标签 / 部分 permission tag。
  // permission 必须与后端路由 RequirePermission 对齐(见 echat-admin-api/router/routes_*.go),改后端要同步这里。
  // 命名规则:英文专名首字母大写;英文与中文之间留空格;`App` / `VIP` / `AB` 统一用法。
  const menuItems: PermissionMenuItem[] = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: '仪表盘',
      permission: 'dashboard.view',
    },

    // 用户运营 — 给运营/客服日常处理 App 端用户的入口。
    {
      key: 'user-ops',
      icon: <TeamOutlined />,
      label: '用户运营',
      children: [
        { key: '/users', label: 'App 用户', permission: 'user.view' },
        { key: '/users/attribution-coverage', label: '归因覆盖率', permission: 'user.view' },
        { key: '/users/retention', label: '活跃留存', permission: 'user.view' },
        { key: '/users/geo', label: '付费用户地理', permission: 'payment.order.view' },
        { key: '/users/currency-grants', label: '发币记录', permission: 'user.view' },
        { key: '/reports', label: '举报处理', permission: 'character.report.view' },
        { key: '/template-reports', label: '模板举报', permission: 'amspire.template_report.view' },
        { key: '/operations/invitation', label: '邀请活动', permission: 'op.invitation.manage' },
        { key: '/operations/email-bind-reward', label: '绑定邮箱奖励', permission: 'op.email_bind.manage' },
        { key: '/operations/register-bonus', label: '注册奖励', permission: 'op.register_bonus.manage' },
        { key: '/operations/inbox-messages', label: '站内信', permission: 'op.inbox.manage' },
      ],
    },

    // 内容管理 — 原"角色管理",避免跟 RBAC 角色重名。
    {
      key: 'content',
      icon: <RobotOutlined />,
      label: '内容管理',
      children: [
        { key: '/amspire/library', label: 'Amspire 模板总库', anyOf: ['amspire.library.view', 'amspire.library.manage', 'amspire.library.publish', 'amspire.library.distribute'] },
        { key: '/amspire', label: 'Amspire 内容', anyOf: ['amspire.content.view', 'amspire.content.manage', 'amspire.ugc.moderate'] },
        { key: '/amspire/section-requests', label: 'Amspire 区块请求统计', anyOf: ['amspire.content.view', 'amspire.content.manage'] },
        { key: '/characters', label: '角色列表', permission: 'character.view' },
        { key: '/characters/audit', label: '角色审核', permission: 'character.audit.view' },
        // 分类 / 标签的写动作走 taxonomy.manage,菜单也按 manage 收敛 — 避免 view 权限用户进去看不到操作。
        { key: '/characters/categories', label: '角色分类', permission: 'character.taxonomy.manage' },
        { key: '/characters/new-characters', label: 'New Characters', permission: 'character.taxonomy.manage' },
        { key: '/characters/tags', label: '角色标签', permission: 'character.taxonomy.manage' },
        { key: '/characters/system-template-variables', label: '系统模板变量', permission: 'character.template.manage' },
        { key: '/characters/template-variables', label: '角色模板变量', permission: 'character.template.manage' },
        { key: '/characters/stats', label: '角色统计', permission: 'stats.character.view' },
      ],
    },

    {
      key: 'payment',
      icon: <DollarOutlined />,
      label: '支付管理',
      children: [
        { key: '/payment/orders', label: '支付订单', permission: 'payment.order.view' },
        { key: '/payment/analytics', label: '订单分析', permission: 'payment.order.view' },
        { key: '/payment/ltv', label: '注册 LTV', permission: 'payment.order.view' },
        { key: '/payment/ltv-breakdown', label: '渠道 / 广告计划 LTV', permission: 'payment.order.view' },
        { key: '/payment/ad-spends', label: '广告消耗', permission: 'payment.order.view' },
        { key: '/payment/cohort-orders', label: '注册批次订单', permission: 'payment.order.view' },
        { key: '/diamond/overview', label: '货币经济总览', permission: 'payment.diamond.view' },
        { key: '/diamond/cohort', label: '货币 cohort 曲线', permission: 'payment.diamond.view' },
        { key: '/diamond/payment-analysis', label: '货币付费分析', permission: 'payment.diamond.view' },
        { key: '/diamond/ledger', label: '货币收支查询', permission: 'payment.diamond.view' },
        { key: '/payment/refunds', label: '退款管理', permission: 'payment.refund.view' },
        { key: '/payment/fulfillments', label: '发货监控', permission: 'payment.fulfillment.view' },
        { key: '/payment/subscriptions', label: '订阅管理', permission: 'payment.subscription.view' },
        { key: '/payment/products', label: '商品配置', permission: 'payment.product.manage' },
        { key: '/payment/webhook-logs', label: 'Webhook 日志', permission: 'payment.webhook.view' },
        { key: '/payment/payment-events', label: '支付事件', permission: 'payment.event.view' },
        { key: '/payment/google-pay-configs', label: 'Google Pay 配置', permission: 'payment.config.google.manage' },
        { key: '/payment/apple-pay-configs', label: 'Apple Pay 配置', permission: 'payment.config.apple.manage' },
        { key: '/payment/appsflyer-configs', label: 'AppsFlyer 回传配置', permission: 'payment.config.appsflyer.manage' },
        { key: '/payment/thinkingdata-configs', label: '数数上报配置', permission: 'payment.config.thinkingdata.manage' },
        { key: '/payment/payment-channels', label: '支付通道管理', permission: 'payment.config.channel.manage' },
        { key: '/payment/payment-channel-groups', label: '支付通道组管理', permission: 'payment.config.group.manage' },
      ],
    },

    // 运营活动 — 原"运营管理",剥离了"邀请活动"和"站内信"(已挪到用户运营)。
    {
      key: 'op-activity',
      icon: <GiftOutlined />,
      label: '运营活动',
      children: [
        { key: '/operations/activities', label: '活动管理', permission: 'op.activity.manage' },
        { key: '/operations/activity-series', label: '活动系列', permission: 'op.activity.manage' },
        { key: '/operations/rewards', label: '奖励管理', permission: 'op.reward.manage' },
        {
          key: '/operations/checkin',
          // 老循环签到配置已被"签到方案"引擎取代,保留入口仅供查看旧数据,菜单标注已废弃。
          label: (
            <Space size={4}>
              签到配置
              <Tag color="default" style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>已废弃</Tag>
            </Space>
          ),
          permission: 'op.checkin.manage',
        },
        { key: '/operations/checkin-plans', label: '签到方案', permission: 'op.checkin.manage' },
        { key: '/operations/checkin-stats', label: '签到统计', permission: 'op.checkin.manage' },
        { key: '/operations/vip', label: 'VIP 配置', permission: 'op.vip.manage' },
        { key: '/operations/tasks', label: '任务配置', permission: 'op.task.manage' },
        { key: '/operations/character-generation', label: 'Smart Mode 设置', permission: 'op.character_gen.view' },
        { key: '/operations/smart-reply-configs', label: '智能回复配置', permission: 'op.smart_reply.view' },
        { key: '/operations/emotion-photo-configs', label: '情绪照片配置', permission: 'op.emotion_photo.view' },
        { key: '/operations/smart-reply-presets', label: '角色预设(Starter)', permission: 'op.smart_reply.view' },
        { key: '/operations/trending-keywords', label: '热词管理', permission: 'op.trending.manage' },
        { key: '/operations/trending-keyword-stats', label: '热词统计', permission: 'op.trending.stats.view' },
        { key: '/operations/scheduled-tasks', label: '定时任务', permission: 'op.scheduled_task.manage' },
        { key: '/operations/announcements', label: '系统公告', permission: 'op.announcement.manage' },
        { key: '/operations/notifications', label: '用户通知', permission: 'op.notification.manage' },
      ],
    },

    {
      key: 'push',
      icon: <SendOutlined />,
      label: '推送',
      children: [
        { key: '/push/stats', label: '推送统计', permission: 'push.stats.view' },
        { key: '/push/templates', label: '推送模板', permission: 'push.template.manage' },
        { key: '/push/tasks', label: '推送任务', permission: 'push.task.view' },
        { key: '/push/test', label: 'Firebase通知测试', permission: 'push.test.execute' },
        { key: '/push/config', label: '推送配置', permission: 'push.config.manage' },
        { key: '/push/recall-logs', label: '召回记录', permission: 'push.recall.view' },
      ],
    },

    // 聊天管理 — 把"长期记忆查看"从系统设置挪过来,因为它是聊天会话相关。
    {
      key: 'rooms',
      icon: <MessageOutlined />,
      label: '聊天管理',
      children: [
        { key: '/rooms', label: '房间列表', permission: 'room.view' },
        { key: '/rooms/chat-stats', label: '聊天统计', permission: 'room.view' },
        { key: '/settings/room-memories', label: '长期记忆查看', permission: 'room.memory.view' },
      ],
    },

    // 提示词日志 — AI 调用渲染后提示词的集中查询(消息/摘要/智能回复)。
    {
      key: 'prompt-logs',
      icon: <FileSearchOutlined />,
      label: '提示词日志',
      children: [
        { key: '/prompt-logs/messages', label: '消息提示词', permission: 'prompt.log.view' },
        { key: '/prompt-logs/summaries', label: '摘要提示词', permission: 'prompt.log.view' },
        { key: '/prompt-logs/smart-replies', label: '智能回复提示词', permission: 'prompt.log.view' },
      ],
    },

    // AI · 记忆 — 散落各处的 AI/Prompt/记忆配置聚成一组。
    {
      key: 'ai-memory',
      icon: <ExperimentOutlined />,
      label: 'AI · 记忆',
      children: [
        { key: '/settings/ai-providers', label: 'AI 提供商', permission: 'config.ai_provider.manage' },
        { key: '/settings/ai-provider-manager-config', label: 'Provider 调度策略', permission: 'config.ai_provider.manage' },
        { key: '/settings/ai-provider-call-logs', label: 'Provider 失败追溯', permission: 'config.ai_provider.manage' },
        { key: '/settings/ai-provider-stats', label: 'Provider 成功率统计', permission: 'config.ai_provider.manage' },
        { key: '/settings/prompt-templates', label: 'Prompt 模板', permission: 'prompt.template.manage' },
        { key: '/settings/memory-tier-presets', label: '长期记忆档位', permission: 'memory.tier.manage' },
        { key: '/characters/ai-configs', label: '角色 AI 配置', permission: 'character.ai_config.manage' },
      ],
    },

    // 图片生成 — 任务执行 + 日志,跟"图片配置"分开。
    {
      key: 'image-gen',
      icon: <PictureOutlined />,
      label: '图片生成',
      children: [
        { key: '/image-generation/tasks', label: '生成任务', permission: 'image.gen.task.view' },
        { key: '/image-generation/translation-records', label: '翻译记录', permission: 'image.translation.view' },
        { key: '/image-generation/lora-library', label: 'Lora 库管理', permission: 'image.gen.lora.manage' },
        { key: '/comfyui-workflow-logs', label: 'ComfyUI 工作流日志', permission: 'comfyui.log.view' },
        { key: '/jimeng-workflow-logs', label: '既梦工作流日志', permission: 'jimeng.log.view' },
      ],
    },

    // 生成 2.0(多引擎)— 原单页 Tabs 拆成多路由子页(req1)。权限默认只 super_admin(req5)。
    {
      key: 'generation-v2',
      icon: <ExperimentOutlined />,
      label: '生成 2.0(多引擎)',
      children: [
        { key: '/generation-v2', label: '应用配置 / AI Writer', permission: 'generation.v2.config.manage' },
        { key: '/generation-v2/free-gen-config', label: '免费预览额度', permission: 'generation.v2.config.manage' },
        { key: '/generation-v2/engine-configs', label: '引擎配置', permission: 'generation.v2.config.manage' },
        { key: '/generation-v2/routing', label: '主页生图路由', permission: 'generation.v2.config.manage' },
        { key: '/generation-v2/models', label: '模型注册', permission: 'generation.v2.config.manage' },
        { key: '/generation-v2/model-groups', label: '模型组', permission: 'generation.v2.config.manage' },
        { key: '/generation-v2/form-fields', label: '表单字段', permission: 'generation.v2.config.manage' },
        { key: '/generation-v2/chat-operations', label: '快捷生图/生视频', permission: 'generation.v2.config.manage' },
        { key: '/generation-v2/cache-whitelist', label: '结果缓存白名单', permission: 'generation.v2.config.manage' },
        { key: '/generation-v2/pricing', label: '计价(可选覆盖)', permission: 'generation.v2.config.manage' },
        { key: '/generation-v2/nodes', label: 'ComfyUI 节点', permission: 'generation.v2.config.manage' },
        { key: '/generation-v2/workflow-templates', label: '工作流模板', permission: 'generation.v2.config.manage' },
        { key: '/generation-v2/tts-voices', label: 'TTS 音色配置', permission: 'generation.v2.config.manage' },
        { key: '/generation-v2/tasks', label: '任务监控', permission: 'generation.v2.task.view' },
        { key: '/generation-v2/comparisons', label: '对比测试', permission: 'generation.v2.task.view' },
        { key: '/generation-v2/user-tasks', label: '用户任务查询', permission: 'generation.v2.task.view' },
        { key: '/generation-stats', label: '生图统计', permission: 'generation.v2.task.view' },
        { key: '/generation-analytics', label: '新版生成分析', permission: 'generation.v2.task.view' },
      ],
    },

    // 图片配置 — 基础配置 + ComfyUI / 既梦集成配置,所有"配置类"放这。
    {
      key: 'image-config',
      icon: <FileImageOutlined />,
      label: '图片配置',
      children: [
        { key: '/image-configs/types', label: '类型配置', permission: 'image.config.type.manage' },
        { key: '/image-configs/models', label: '模型配置', permission: 'image.config.model.manage' },
        { key: '/image-configs/styles', label: '风格配置', permission: 'image.config.style.manage' },
        { key: '/image-configs/tags', label: '标签配置', permission: 'image.config.tag.manage' },
        { key: '/settings/workflow-templates', label: '工作流模板', permission: 'image.workflow.template.manage' },
        { key: '/settings/comfyui-servers', label: 'ComfyUI 服务器', permission: 'comfyui.server.manage' },
        { key: '/settings/jimeng-configs', label: '既梦 SDK 配置', permission: 'jimeng.config.manage' },
        { key: '/jimeng-whitelist', label: '既梦白名单', permission: 'jimeng.whitelist.manage' },
      ],
    },

    // 应用配置 — App 维度的配置 + 公共图库 + 告警机器人 + 应用访问控制(原 IP 访问控制)。
    {
      key: 'app-config',
      icon: <AppstoreOutlined />,
      label: '应用配置',
      children: [
        { key: '/settings/apps', label: 'App 管理', permission: 'config.app.manage' },
        { key: '/settings/app-settings', label: '应用开关配置', permission: 'config.app.manage' },
        { key: '/settings/versions', label: 'App 版本管理', permission: 'config.version.manage' },
        { key: '/settings/app-store-links', label: '应用商店链接', permission: 'config.app_store_link.manage' },
        { key: '/settings/firebase', label: 'Firebase 配置', permission: 'config.firebase.manage' },
        { key: '/settings/onelink-configs', label: 'OneLink 配置', permission: 'config.onelink.manage' },
        { key: '/settings/app-ab-configs', label: 'AB 实验配置', permission: 'config.ab.manage' },
        { key: '/settings/upload-configs', label: '上传配置', permission: 'config.upload.manage' },
        { key: '/settings/admin-upload-config', label: '后台上传配置', permission: 'config.upload.manage' },
        { key: '/settings/app-costs', label: '应用费用配置', permission: 'config.app_cost.view' },
        { key: '/settings/free-chat-configs', label: '免费聊天配置', permission: 'config.free_chat.manage' },
        { key: '/settings/base-limits', label: '基础限制配置', permission: 'config.base_limit.manage' },
        { key: '/settings/service-accounts', label: '服务账号管理', permission: 'config.service_account.manage' },
        { key: '/settings/translation-configs', label: '翻译配置', permission: 'config.translation.manage' },
        { key: '/settings/image-library', label: '公共图库', permission: 'config.image_library.manage' },
        { key: '/settings/alert-bots', label: '告警机器人', permission: 'config.alert_bot.manage' },
        { key: '/moderation/access-control', label: '应用访问控制', permission: 'security.access_control.view' },
      ],
    },

    // 系统管理 — 后台账号体系 + RBAC + 全局系统开关。
    {
      key: 'system',
      icon: <SafetyOutlined />,
      label: '系统管理',
      children: [
        { key: '/users/admins', label: '管理员', permission: 'admin.account.view' },
        { key: '/users/roles', label: 'RBAC 角色', permission: 'admin.role.view' },
        { key: '/users/permissions', label: '权限总览', permission: 'admin.role.view' },
        { key: '/settings/system-configs', label: '系统配置', permission: 'config.system.view' },
        { key: '/system/api-monitor', label: '接口请求监控', permission: 'api_monitor.view' },
      ],
    },

    // 审计 — 所有审计入口聚成一级,合规检查只需打开这一组。
    {
      key: 'audit',
      icon: <AuditOutlined />,
      label: '审计',
      children: [
        { key: '/settings/operation-logs', label: '操作日志', permission: 'audit.operation_log.view' },
        { key: '/audit/permission-changes', label: '权限变更审计', permission: 'audit.permission_change.view' },
      ],
    },
  ]

  // 权限码不变 / 菜单结构不变时,filterMenuByPermission 的输出是稳定的,缓存住避免每次 setState 重跑。
  const filteredMenuItems = useMemo(
    () => filterMenuByPermission(menuItems, permissions),
    // menuItems 是字面常量数组,引用变化等价于刷新页面;权限变化才是真正会变的输入。
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permissions],
  )

  const handleMenuClick = (e: { key: string }) => {
    // 切换页面时把当前 app_id 一并带上,保证应用上下文跨页面不丢失。
    // 优先取 URL 的 app_id;若当前页走的是历史 ?package= 流程(URL 无 app_id),回退到 store。
    const appId = searchParams.get('app_id') ?? selectedApp?.id
    navigate(appId ? `${e.key}?app_id=${appId}` : e.key)
  }

  // 处理子菜单展开/折叠
  const handleOpenChange = (keys: string[]) => {
    // 找到最新打开的 key
    const latestOpenKey = keys.find(key => openKeys.indexOf(key) === -1)

    if (latestOpenKey && rootSubmenuKeys.indexOf(latestOpenKey) !== -1) {
      // 打开了一个根菜单 - 使用手风琴模式，只保留最新打开的
      setOpenKeys([latestOpenKey])
    } else {
      // 如果用户关闭了菜单，允许关闭；否则保持当前打开的菜单
      setOpenKeys(keys)
    }
  }

  // 根据当前路径自动展开对应的父菜单
  useEffect(() => {
    // 根据 pathname 找到对应的父菜单
    for (const item of menuItems) {
      if (item && 'children' in item && item.children) {
        const hasChild = item.children.some((child: MenuItem) => child && 'key' in child && child.key === pathname)
        if (hasChild && item.key) {
          setOpenKeys([item.key as string])
          break
        }
      }
    }
  }, [pathname])

  // 获取当前选中的菜单项
  const selectedKey = pathname

  // 如果正在加载或未登录，显示加载状态
  if (loading || !user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">加载中...</p>
        </div>
      </div>
    )
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={240}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
      >
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: collapsed ? 16 : 20,
            fontWeight: 'bold',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          {collapsed ? 'EC' : 'EChat Admin'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          openKeys={openKeys}
          onOpenChange={handleOpenChange}
          items={filteredMenuItems}
          onClick={handleMenuClick}
          style={{ borderRight: 0 }}
        />
      </Sider>
      <Layout style={{ marginLeft: collapsed ? 80 : 240, transition: 'all 0.2s' }}>
        <Header
          style={{
            padding: '0 24px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #f0f0f0',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <Space>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                fontSize: '16px',
                width: 40,
                height: 40,
              }}
            />
          </Space>

          <Space size="large">
            {/* 应用选择器 */}
            <Space>
              <AppstoreOutlined style={{ fontSize: 16, color: '#1890ff' }} />
              <Select
                value={selectedApp?.id}
                onChange={(appId) => {
                  // 选中:只改 URL,store 由 URL→store 同步 effect 更新(单向数据流,避免双事实源)。
                  // 清空:同步 effect 不处理 app_id 缺失,这里直接把 store 置空,保证 URL 与 store 一致。
                  setSearchParams(
                    (prev) => {
                      if (appId) prev.set('app_id', appId)
                      else prev.delete('app_id')
                      return prev
                    },
                    { replace: true },
                  )
                  if (!appId) setSelectedApp(null)
                }}
                placeholder="选择应用"
                style={{ width: 250 }}
                showSearch
                allowClear
                onClear={() => {
                  setSearchParams(
                    (prev) => {
                      prev.delete('app_id')
                      return prev
                    },
                    { replace: true },
                  )
                  setSelectedApp(null)
                }}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={appList.map(app => ({
                  label: app.name ? `${app.name} (${app.package_name})` : app.package_name,
                  value: app.id,
                }))}
              />
            </Space>

            <Badge count={5}>
              <BellOutlined style={{ fontSize: 18, cursor: 'pointer' }} />
            </Badge>
            <Dropdown menu={{ items: userMenuItems, onClick: handleUserMenuClick }} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                <Avatar icon={<UserOutlined />} />
                <span>{user?.username || '管理员'}</span>
              </Space>
            </Dropdown>
          </Space>
        </Header>
        <Content
          style={{
            margin: '24px',
            padding: 24,
            minHeight: 280,
            background: '#fff',
            borderRadius: 8,
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  )
}
